// AI Opponent Logic - Enhanced with Strategic Decision Making
import { checkPrerequisites } from './CardRulesEngine';

// AI difficulty levels with progression
export const AI_DIFFICULTY = {
  EASY: 'easy',
  MEDIUM: 'medium', 
  HARD: 'hard',
  EXPERT: 'expert'
};

// Card synergy chains for strategic planning
const CARD_CHAINS = {
  // Eco chain
  'Seed': ['Sapling', 'Garden'],
  'Sapling': ['Tree'],
  'Tree': ['Forest', 'Bloom'],
  'Forest': ['Thriving Ecosystem', 'Habitat'],
  'Bloom': ['Sapling', 'Tree', 'Forest', 'Wildflowers'],
  
  // Tech chain
  'Materials': ['Tools', 'Battery'],
  'Tools': ['Battery'],
  'Battery': ['Solar Panel', 'Wind Turbine', 'Nuclear Energy'],
  'Engineer': ['Solar Panel', 'Wind Turbine', 'Nuclear Energy'],
  'Settlement': ['Factory', 'City'],
  'Factory': ['City'],
  
  // Wisdom chain
  'Meditation': ['Patience'],
  'Patience': ['Love', 'Tree', 'Garden', 'Oasis'],
  'Love': ['Thriving Ecosystem', 'Thriving Civilization', 'Community', 'Thriving Spirit'],
  'Curiosity': ['Explorer', 'Physicist', 'Thriving Spirit'],
  'Naturalist': ['Garden', 'Habitat', 'Oasis'],
};

// High value target cards to prioritize
const HIGH_VALUE_CARDS = [
  'Thriving Ecosystem', 'Thriving Civilization', 'Thriving Spirit',
  'Forest', 'Nuclear Energy', 'Love', 'City', 'Oasis', 'Farm'
];

// Key prerequisite cards that unlock many options
const KEY_ENABLERS = [
  'Meditation', 'Seed', 'Materials', 'Tools', 'Curiosity', 
  'Engineer', 'Naturalist', 'Patience', 'Battery'
];

// Cards that provide spaces (critical for late game)
const SPACE_CARDS = [
  'Love', 'Settlement', 'Habitat', 'Oasis', 'Uncharted', 
  'Community', 'City', 'Thriving Civilization'
];

// Get difficulty from games played (progressive system)
export const getDifficultyFromGamesPlayed = (gamesPlayed) => {
  if (gamesPlayed < 3) return AI_DIFFICULTY.EASY;
  if (gamesPlayed < 7) return AI_DIFFICULTY.MEDIUM;
  if (gamesPlayed < 15) return AI_DIFFICULTY.HARD;
  return AI_DIFFICULTY.EXPERT;
};

export class AIOpponent {
  constructor(difficulty = AI_DIFFICULTY.MEDIUM) {
    this.difficulty = difficulty;
    this.turnCount = 0;
    this.gamePhase = 'early'; // early, mid, late
  }

  // Main decision function - AI decides what to do on its turn
  async makeDecision(gameState, aiPlayerId) {
    // Add delay to simulate thinking (varies by difficulty)
    await this.simulateThinking();

    const aiPlayer = gameState.getPlayerById(aiPlayerId);
    if (!aiPlayer) return null;

    this.turnCount++;
    this.updateGamePhase(aiPlayer, gameState);

    // Evaluate all possible actions with strategic context
    const actions = this.evaluateActions(gameState, aiPlayer);

    // Choose best action based on difficulty and strategy
    const chosenAction = this.selectAction(actions);

    return chosenAction;
  }

  // Update game phase based on game state
  updateGamePhase(player, gameState) {
    const spacesUsed = player.usedSpaces / player.spaces;
    const deckRemaining = player.deck.length;
    
    if (spacesUsed < 0.3 && deckRemaining > 20) {
      this.gamePhase = 'early';
    } else if (spacesUsed < 0.7 && deckRemaining > 8) {
      this.gamePhase = 'mid';
    } else {
      this.gamePhase = 'late';
    }
  }

  // Simulate AI thinking time (harder AI thinks longer)
  async simulateThinking() {
    const baseTime = {
      [AI_DIFFICULTY.EASY]: 400,
      [AI_DIFFICULTY.MEDIUM]: 700,
      [AI_DIFFICULTY.HARD]: 1000,
      [AI_DIFFICULTY.EXPERT]: 1300
    }[this.difficulty] || 700;
    
    // Add some variance
    const variance = Math.random() * 300;
    return new Promise(resolve => setTimeout(resolve, baseTime + variance));
  }

  // Evaluate all possible actions with full context
  evaluateActions(gameState, aiPlayer) {
    const actions = [];
    const centerCards = gameState.getCenterCards ? gameState.getCenterCards() : [];
    const opponents = gameState.getOpponents ? gameState.getOpponents() : [];
    const opponent = opponents[0]; // Main opponent

    // Evaluate playing each card from hand
    aiPlayer.hand.forEach((card, index) => {
      const score = this.evaluatePlayingCard(card, aiPlayer, gameState, centerCards, opponent);
      if (score > -500) { // Only add if potentially valid
        actions.push({
          type: 'play_from_hand',
          cardIndex: index,
          card: card,
          score: score
        });
      }
    });

    // Evaluate discarding each card
    aiPlayer.hand.forEach((card, index) => {
      const score = this.evaluateDiscarding(card, aiPlayer, gameState, opponent);
      actions.push({
        type: 'discard',
        cardIndex: index,
        card: card,
        score: score
      });
    });

    // Evaluate playing ONLY the top card from opponent's discard pile
    // Only the top card (last in the array) is available to play
    if (opponent && opponent.discardPile && opponent.discardPile.length > 0) {
      const topCardIndex = opponent.discardPile.length - 1;
      const topCard = opponent.discardPile[topCardIndex];
      const score = this.evaluatePlayingOpponentCard(topCard, aiPlayer, opponent, gameState, centerCards);
      if (score > -500) {
        actions.push({
          type: 'play_from_opponent',
          opponentId: opponent.id,
          cardIndex: topCardIndex,
          card: topCard,
          score: score
        });
      }
    }

    return actions;
  }

  // Enhanced card evaluation with strategic thinking
  evaluatePlayingCard(card, player, gameState, centerCards, opponent) {
    // Check basic validity
    if (player.usedSpaces >= player.spaces) {
      return -1000;
    }

    if (!checkPrerequisites(card, player.playedCards, centerCards)) {
      return -1000;
    }

    let score = 0;
    const allPlayedCards = [...player.playedCards, ...centerCards];

    // === ENERGY VALUE ===
    if (card.energy > 0) {
      // Scale energy value based on game phase
      const energyMultiplier = this.gamePhase === 'late' ? 15 : 
                               this.gamePhase === 'mid' ? 12 : 10;
      score += card.energy * energyMultiplier;
    } else if (card.energy < 0) {
      // Penalize negative energy less in early game (building foundation)
      const penaltyMultiplier = this.gamePhase === 'early' ? 3 : 
                                this.gamePhase === 'mid' ? 5 : 8;
      score += card.energy * penaltyMultiplier;
    }

    // === HIGH VALUE TARGETS ===
    if (HIGH_VALUE_CARDS.includes(card.name)) {
      score += 50;
    }

    // === SPACE MANAGEMENT ===
    if (SPACE_CARDS.includes(card.name)) {
      // More valuable when spaces are running low
      const spacesRemaining = player.spaces - player.usedSpaces;
      if (spacesRemaining <= 3) {
        score += 80;
      } else if (spacesRemaining <= 5) {
        score += 50;
      } else {
        score += 25;
      }
    }

    // === CARD DRAW ABILITIES ===
    if (card.description && card.description.toLowerCase().includes('draw')) {
      // More valuable in early/mid game
      score += this.gamePhase === 'late' ? 15 : 35;
    }

    // === DECK BUILDING ABILITIES ===
    if (card.description && card.description.toLowerCase().includes('deck')) {
      score += this.gamePhase === 'late' ? 10 : 30;
    }

    // === CHAIN ENABLERS ===
    if (KEY_ENABLERS.includes(card.name)) {
      // Check how many cards in hand this enables
      const enabledCards = this.countEnabledCards(card, player.hand, allPlayedCards);
      score += enabledCards * 20;
      
      // Early game bonus for enablers
      if (this.gamePhase === 'early') {
        score += 25;
      }
    }

    // === SYNERGY BONUS ===
    score += this.calculateSynergyBonus(card, player, allPlayedCards);

    // === SPECIAL CARD STRATEGIES ===
    score += this.evaluateSpecialCard(card, player, gameState);

    // === OPPONENT DENIAL (Hard+ only) ===
    if (this.difficulty === AI_DIFFICULTY.HARD || this.difficulty === AI_DIFFICULTY.EXPERT) {
      if (opponent) {
        score += this.evaluateOpponentDenial(card, opponent);
      }
    }

    // === DIFFICULTY MODIFIERS ===
    score = this.applyDifficultyModifiers(score, card);

    return score;
  }

  // Calculate synergy bonus based on existing cards
  calculateSynergyBonus(card, player, allPlayedCards) {
    let bonus = 0;
    
    // Check if this card enables chains for cards in hand
    const chains = CARD_CHAINS[card.name] || [];
    player.hand.forEach(handCard => {
      if (chains.includes(handCard.name)) {
        bonus += 15; // Bonus for enabling a card we have
      }
    });

    // Bonus for completing chains we've started
    const playedNames = allPlayedCards.map(c => c.name);
    Object.entries(CARD_CHAINS).forEach(([prereq, unlocks]) => {
      if (playedNames.includes(prereq) && unlocks.includes(card.name)) {
        bonus += 10; // Following our chain
      }
    });

    // Type synergy - having multiple of same type
    const sameTypeCount = allPlayedCards.filter(c => c.type === card.type).length;
    bonus += sameTypeCount * 3;

    return bonus;
  }

  // Evaluate special card-specific strategies
  evaluateSpecialCard(card, player, gameState) {
    let bonus = 0;

    switch (card.name) {
      case 'Factory':
        // Value based on Materials cards played
        const materialsCount = player.playedCards.filter(c => c.name === 'Materials').length;
        bonus += materialsCount * 25;
        break;
        
      case 'Bloom':
        // High value for eco chain
        bonus += 40;
        break;
        
      case 'Observation':
        // Better when deck has more cards
        if (player.deck.length > 15) bonus += 25;
        break;
        
      case 'Scavenger':
        // Value based on discard pile contents
        const goodDiscards = player.discardPile.filter(c => c.energy > 0).length;
        bonus += goodDiscards * 10;
        break;
        
      case 'Futurist':
        // Always valuable for finding key cards
        bonus += 30;
        break;
        
      case 'Thriving Spirit':
        // Extremely high value combo card
        bonus += 60;
        break;
        
      case 'Community':
        // Good for hand and space advantage
        bonus += 35;
        break;
        
      case 'Explorer':
        if (player.deck.length > 10) bonus += 20;
        break;
      default:
        break;
    }

    return bonus;
  }

  // Evaluate denial value against opponent (Hard+ AI)
  evaluateOpponentDenial(card, opponent) {
    let denial = 0;

    // Check if opponent might need cards that this card enables
    // If opponent has cards in hand that need this as prereq
    // (we can infer from their played cards what they might be building)
    const opponentPlayedNames = opponent.playedCards.map(c => c.name);
    
    // If opponent is building eco chain and we can advance our own
    if (opponentPlayedNames.includes('Seed') || opponentPlayedNames.includes('Sapling')) {
      if (['Tree', 'Forest', 'Bloom'].includes(card.name)) {
        denial += 15;
      }
    }
    
    // If opponent is building tech chain
    if (opponentPlayedNames.includes('Engineer') || opponentPlayedNames.includes('Battery')) {
      if (['Solar Panel', 'Wind Turbine', 'Nuclear Energy'].includes(card.name)) {
        denial += 15;
      }
    }

    return denial;
  }

  // Enhanced discard evaluation
  evaluateDiscarding(card, player, gameState, opponent) {
    let score = 5; // Base score for discarding

    // === NEVER DISCARD HIGH VALUE ===
    if (HIGH_VALUE_CARDS.includes(card.name)) {
      return -50; // Strongly avoid
    }

    // === PREFER DISCARDING UNPLAYABLE CARDS ===
    const centerCards = gameState.getCenterCards ? gameState.getCenterCards() : [];
    const canPlay = checkPrerequisites(card, player.playedCards, centerCards);
    
    if (!canPlay) {
      // Check if we're building towards it
      const willBePlayable = this.willCardBePlayableSoon(card, player);
      if (!willBePlayable) {
        score += 30; // Good candidate for discard
      }
    }

    // === NEGATIVE ENERGY CARDS ARE OK TO DISCARD ===
    if (card.energy < 0 && !KEY_ENABLERS.includes(card.name)) {
      score += 20;
    }

    // === DUPLICATE CARDS ===
    const duplicateCount = player.hand.filter(c => c.name === card.name).length;
    if (duplicateCount > 1) {
      score += 25; // Ok to discard duplicates
    }

    // === DEAD ENDS ===
    // If prerequisites for this card aren't in hand or played, lower priority
    if (card.type === 'Tech' && card.name !== 'Materials' && card.name !== 'Tools') {
      const hasTechBase = player.playedCards.some(c => 
        ['Materials', 'Tools', 'Battery', 'Engineer'].includes(c.name)
      );
      if (!hasTechBase) {
        score += 15;
      }
    }

    // === LATE GAME: DISCARD LOW VALUE MORE ===
    if (this.gamePhase === 'late' && card.energy <= 1) {
      score += 15;
    }

    // === OPPONENT CONSIDERATION (Hard+) ===
    if (this.difficulty === AI_DIFFICULTY.HARD || this.difficulty === AI_DIFFICULTY.EXPERT) {
      // Don't discard cards opponent could use well
      if (opponent && this.wouldHelpOpponent(card, opponent)) {
        score -= 20;
      }
    }

    return score;
  }

  // Check if card will be playable soon based on hand contents
  willCardBePlayableSoon(card, player) {
    const handNames = player.hand.map(c => c.name);
    const playedNames = player.playedCards.map(c => c.name);
    const allAvailable = [...handNames, ...playedNames];

    // Simple heuristic: check if common prerequisites are available
    switch (card.name) {
      case 'Sapling':
        return allAvailable.includes('Seed');
      case 'Tree':
        return allAvailable.includes('Sapling') || 
               (allAvailable.includes('Seed') && allAvailable.includes('Patience'));
      case 'Forest':
        return allAvailable.includes('Tree');
      case 'Patience':
        return allAvailable.includes('Meditation');
      case 'Battery':
        return allAvailable.includes('Materials') && allAvailable.includes('Tools');
      default:
        return false;
    }
  }

  // Check if discarding a card would significantly help opponent
  wouldHelpOpponent(card, opponent) {
    const opponentPlayedNames = opponent.playedCards.map(c => c.name);
    
    // Check if this card completes opponent's chain
    if (opponentPlayedNames.includes('Seed') && card.name === 'Sapling') return true;
    if (opponentPlayedNames.includes('Sapling') && card.name === 'Tree') return true;
    if (opponentPlayedNames.includes('Tree') && card.name === 'Forest') return true;
    if (opponentPlayedNames.includes('Meditation') && card.name === 'Patience') return true;
    if (opponentPlayedNames.includes('Patience') && card.name === 'Love') return true;
    
    return false;
  }

  // Enhanced evaluation for opponent's discard pile cards
  evaluatePlayingOpponentCard(card, player, opponent, gameState, centerCards) {
    // Check validity
    if (player.usedSpaces >= player.spaces) {
      return -1000;
    }

    // Wisdom cards (except Love) can't be shared
    if (card.type === 'Wisdom' && card.name !== 'Love') {
      return -1000;
    }

    // Check prerequisites (use player's cards + center cards)
    if (!checkPrerequisites(card, player.playedCards, centerCards)) {
      return -1000;
    }

    let score = 0;

    // === ENERGY VALUE ===
    if (card.energy > 0) {
      score += card.energy * 12;
    }

    // === HIGH VALUE STEALS ===
    if (HIGH_VALUE_CARDS.includes(card.name)) {
      score += 60;
    }

    // === DENIAL VALUE ===
    // Playing from opponent's discard denies them using it later
    if (KEY_ENABLERS.includes(card.name)) {
      score += 30;
    }

    // === SHARED PREREQ BONUS ===
    // Center cards count as prereqs for both - consider if this helps opponent too
    const chains = CARD_CHAINS[card.name] || [];
    const opponentMightUse = opponent.hand && chains.some(chainCard => 
      opponent.discardPile.some(d => d.name === chainCard)
    );
    
    if (!opponentMightUse) {
      score += 15; // Safe to take, doesn't help opponent much
    }

    // === SYNERGY WITH OUR BOARD ===
    score += this.calculateSynergyBonus(card, player, [...player.playedCards, ...centerCards]);

    return score;
  }

  // Apply difficulty-based modifiers
  applyDifficultyModifiers(score, card) {
    switch (this.difficulty) {
      case AI_DIFFICULTY.EASY:
        // Add significant randomness
        score += (Math.random() - 0.5) * 50;
        // Sometimes completely miss good plays
        if (Math.random() < 0.2 && score > 50) {
          score *= 0.5;
        }
        break;
        
      case AI_DIFFICULTY.MEDIUM:
        // Add moderate randomness
        score += (Math.random() - 0.5) * 25;
        break;
        
      case AI_DIFFICULTY.HARD:
        // Slight randomness for variety
        score += (Math.random() - 0.5) * 10;
        break;
        
      case AI_DIFFICULTY.EXPERT:
        score += (Math.random() - 0.5) * 5;
        break;
      default:
        break;
    }
    
    return score;
  }

  // Count how many cards in hand this card would enable
  countEnabledCards(card, hand, playedCards) {
    const wouldBePlayed = [...playedCards, card];
    let count = 0;
    
    hand.forEach(handCard => {
      if (handCard.name !== card.name) {
        const currentlyPlayable = checkPrerequisites(handCard, playedCards, []);
        const wouldBePlayable = checkPrerequisites(handCard, wouldBePlayed, []);
        if (!currentlyPlayable && wouldBePlayable) {
          count++;
        }
      }
    });
    
    return count;
  }

  // Select action based on difficulty with improved logic
  selectAction(actions) {
    if (actions.length === 0) return null;

    // Filter to valid actions only
    const validActions = actions.filter(a => a.score > -500);
    if (validActions.length === 0) {
      // Must discard if nothing else valid
      const discardActions = actions.filter(a => a.type === 'discard');
      if (discardActions.length > 0) {
        discardActions.sort((a, b) => b.score - a.score);
        return discardActions[0];
      }
      return actions[0];
    }

    // Sort by score (highest first)
    validActions.sort((a, b) => b.score - a.score);

    // Prefer playing over discarding when scores are close
    const bestPlay = validActions.find(a => a.type !== 'discard');
    const bestDiscard = validActions.find(a => a.type === 'discard');
    
    if (bestPlay && bestDiscard) {
      // Only discard if significantly better
      const playThreshold = {
        [AI_DIFFICULTY.EASY]: 10,
        [AI_DIFFICULTY.MEDIUM]: 15,
        [AI_DIFFICULTY.HARD]: 20,
        [AI_DIFFICULTY.EXPERT]: 25
      }[this.difficulty] || 15;
      
      if (bestPlay.score + playThreshold >= bestDiscard.score) {
        return bestPlay;
      }
    }

    // Selection based on difficulty
    switch (this.difficulty) {
      case AI_DIFFICULTY.EASY:
        // 40% chance to pick from top 50%
        if (Math.random() < 0.4) {
          const topHalf = Math.ceil(validActions.length / 2);
          return validActions[Math.floor(Math.random() * topHalf)];
        }
        break;
        
      case AI_DIFFICULTY.MEDIUM:
        // 25% chance to pick from top 3
        if (Math.random() < 0.25 && validActions.length > 1) {
          const topCount = Math.min(3, validActions.length);
          return validActions[Math.floor(Math.random() * topCount)];
        }
        break;
        
      case AI_DIFFICULTY.HARD:
        // 10% chance to pick second best
        if (Math.random() < 0.1 && validActions.length > 1) {
          return validActions[1];
        }
        break;
        
      case AI_DIFFICULTY.EXPERT:
        break;
      default:
        break;
    }

    return validActions[0];
  }

  // Get AI decision as a readable string
  getDecisionDescription(action) {
    if (!action) return 'Pass';

    switch (action.type) {
      case 'play_from_hand':
        return `Play ${action.card.name} from hand`;
      case 'discard':
        return `Discard ${action.card.name}`;
      case 'play_from_opponent':
        return `Play ${action.card.name} from opponent's discard`;
      default:
        return 'Unknown action';
    }
  }

  // Get difficulty description
  static getDifficultyDescription(difficulty) {
    switch (difficulty) {
      case AI_DIFFICULTY.EASY:
        return 'Beginner friendly - makes occasional mistakes';
      case AI_DIFFICULTY.MEDIUM:
        return 'Balanced - plays well but not perfectly';
      case AI_DIFFICULTY.HARD:
        return 'Challenging - strategic and efficient';
      case AI_DIFFICULTY.EXPERT:
        return 'Master - optimal play, minimal mistakes';
      default:
        return 'Unknown difficulty';
    }
  }
}

export default AIOpponent;
